import { collection, setDoc, updateDoc, deleteDoc, doc, getDocs, query, orderBy, where } from "firebase/firestore";
import { db } from "./firebase";
import { financialCategoryToFirestore } from "../models/financialCategory";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "./collections";
import { FINANCIAL_TYPES } from "../constants/financial";

/**
 * Categorias padrão para o Plano de Contas (DRE).
 * Criadas automaticamente quando um tenant é configurado pela primeira vez.
 */
const DEFAULT_CATEGORIES = [
    // ═══ RECEITAS (INCOME) ═══
    { name: "Mensalidades", type: FINANCIAL_TYPES.INCOME, order: 1, isSystem: true },
    { name: "Taxas de Matrícula", type: FINANCIAL_TYPES.INCOME, order: 2, isSystem: true },
    { name: "Serviços Avulsos", type: FINANCIAL_TYPES.INCOME, order: 3, isSystem: true },
    { name: "Produtos / Bar", type: FINANCIAL_TYPES.INCOME, order: 4, isSystem: true },
    { name: "Eventos", type: FINANCIAL_TYPES.INCOME, order: 5, isSystem: true },
    { name: "Estornos e Reembolsos Recebidos", type: FINANCIAL_TYPES.INCOME, order: 6, isSystem: true },
    { name: "Outras Receitas", type: FINANCIAL_TYPES.INCOME, order: 7, isSystem: true },

    // ═══ DESPESAS (EXPENSE) ═══
    { name: "Taxas de Cartão / Gateway", type: FINANCIAL_TYPES.EXPENSE, order: 10, isSystem: true },
    { name: "Comissões", type: FINANCIAL_TYPES.EXPENSE, order: 11, isSystem: true },
    { name: "Estornos e Devoluções a Clientes", type: FINANCIAL_TYPES.EXPENSE, order: 12, isSystem: true },
    { name: "Salários (CLT)", type: FINANCIAL_TYPES.EXPENSE, order: 20, isSystem: true },
    { name: "Pró-Labore / Retiradas", type: FINANCIAL_TYPES.EXPENSE, order: 21, isSystem: true },
    { name: "Prestadores de Serviço (PJ/Freelancer)", type: FINANCIAL_TYPES.EXPENSE, order: 22, isSystem: true },
    { name: "Aluguel e Condomínio", type: FINANCIAL_TYPES.EXPENSE, order: 23, isSystem: true },
    { name: "Energia / Água / Internet", type: FINANCIAL_TYPES.EXPENSE, order: 24, isSystem: true },
    { name: "Manutenção e Limpeza", type: FINANCIAL_TYPES.EXPENSE, order: 25, isSystem: true },
    { name: "Marketing e Publicidade", type: FINANCIAL_TYPES.EXPENSE, order: 26, isSystem: true },
    { name: "Material / Suprimentos", type: FINANCIAL_TYPES.EXPENSE, order: 27, isSystem: true },
    { name: "Impostos e Tributos (DAS/INSS/Receita)", type: FINANCIAL_TYPES.EXPENSE, order: 28, isSystem: true },
    { name: "Combustível e Transporte", type: FINANCIAL_TYPES.EXPENSE, order: 29, isSystem: true },
    { name: "Contabilidade e Assessorias", type: FINANCIAL_TYPES.EXPENSE, order: 30, isSystem: true },
    { name: "Alimentação e Refeição", type: FINANCIAL_TYPES.EXPENSE, order: 31, isSystem: true },
    { name: "Outras Despesas", type: FINANCIAL_TYPES.EXPENSE, order: 32, isSystem: true },

    // ═══ ATIVOS (ASSET) ═══
    { name: "Caixa (Dinheiro)", type: FINANCIAL_TYPES.ASSET, order: 40, isSystem: true },
    { name: "Contas a Receber", type: FINANCIAL_TYPES.ASSET, order: 41, isSystem: true },
    { name: "Bancos / Conta Corrente", type: FINANCIAL_TYPES.ASSET, order: 42, isSystem: true },

    // ═══ PASSIVOS (LIABILITY) ═══
    { name: "Adiantamento de Clientes", type: FINANCIAL_TYPES.LIABILITY, order: 50, isSystem: true },
];

/**
 * FinancialCategoryService
 * Centraliza as operações de CRUD para o Plano de Contas (DRE).
 * 
 * Coleção: tenants/{idTenant}/financialCategories
 */
const FinancialCategoryService = {

    /**
     * Busca todas as categorias financeiras de um tenant.
     * Ordenadas por tipo (INCOME primeiro) e depois por ordem.
     */
    getAll: async (idTenant) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_CATEGORIES),
                orderBy("order", "asc")
            );
            const querySnapshot = await getDocs(q);
            const categories = [];
            querySnapshot.forEach((doc) => {
                categories.push({ id: doc.id, ...doc.data() });
            });
            return categories;
        } catch (error) {
            console.error("Erro ao listar categorias financeiras:", error);
            throw error;
        }
    },

    /**
     * Busca categorias por tipo (INCOME ou EXPENSE).
     */
    getByType: async (idTenant, type) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_CATEGORIES),
                where("type", "==", type),
                orderBy("order", "asc")
            );
            const querySnapshot = await getDocs(q);
            const categories = [];
            querySnapshot.forEach((doc) => {
                categories.push({ id: doc.id, ...doc.data() });
            });
            return categories;
        } catch (error) {
            console.error("Erro ao listar categorias por tipo:", error);
            throw error;
        }
    },

    /**
     * Salva ou atualiza uma categoria financeira.
     */
    save: async (formData, idTenant, authUser, existingCategories = []) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const categoryData = financialCategoryToFirestore(formData, { id: idTenant }, authUser);

        try {
            if (formData.id) {
                // UPDATE
                const categoryRef = doc(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_CATEGORIES, formData.id);
                const oldCategory = existingCategories.find(c => c.id === formData.id) || {};
                await updateDoc(categoryRef, categoryData);

                // Auditoria
                const changes = AuditService.calculateDiff(oldCategory, categoryData);
                if (changes) {
                    AuditService.log({
                        idTenant,
                        user: authUser,
                        module: AUDIT_MODULES.FINANCIAL_CATEGORIES,
                        action: AUDIT_ACTIONS.UPDATE,
                        entityId: formData.id,
                        entityName: categoryData.name,
                        description: `Atualizou a categoria financeira "${categoryData.name}"`,
                        details: changes
                    });
                }

                return { id: formData.id, ...categoryData };
            } else {
                // CREATE
                const customId = Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
                const newDocRef = doc(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_CATEGORIES, customId);
                await setDoc(newDocRef, categoryData);

                // Auditoria
                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.FINANCIAL_CATEGORIES,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: categoryData.name,
                    description: `Criou a categoria financeira "${categoryData.name}"`
                });

                return { id: newDocRef.id, ...categoryData };
            }
        } catch (error) {
            console.error("Erro no FinancialCategoryService.save:", error);
            throw error;
        }
    },

    /**
     * Remove uma categoria financeira.
     * Categorias com isSystem: true NÃO podem ser excluídas.
     */
    delete: async (categoryId, idTenant, authUser, categoryName) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_CATEGORIES, categoryId));

            // Auditoria
            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.FINANCIAL_CATEGORIES,
                action: AUDIT_ACTIONS.DELETE,
                entityId: categoryId,
                entityName: categoryName,
                description: `Excluiu a categoria financeira "${categoryName || categoryId}"`
            });

            return true;
        } catch (error) {
            console.error("Erro no FinancialCategoryService.delete:", error);
            throw error;
        }
    },

    /**
     * Cria as categorias padrão para um tenant que ainda não tem nenhuma.
     * Deve ser chamada na primeira configuração do tenant ou via Settings.
     * 
     * @param {string} idTenant - ID do tenant
     * @param {Object} authUser - Usuário que está fazendo o seed
     * @returns {number} Quantidade de categorias criadas
     */
    seedDefaults: async (idTenant, authUser) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        try {
            // Verifica se já existem categorias
            const existing = await FinancialCategoryService.getAll(idTenant);
            if (existing.length > 0) {
                console.info("[FinancialCategoryService] Tenant já possui categorias. Seed ignorado.");
                return 0;
            }

            // Cria todas as categorias padrão
            let count = 0;
            for (const category of DEFAULT_CATEGORIES) {
                const categoryData = financialCategoryToFirestore(category, { id: idTenant }, authUser);
                const customId = Date.now().toString(36) + Math.random().toString(36).substring(2, 10);
                await setDoc(
                    doc(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_CATEGORIES, customId),
                    categoryData
                );
                count++;
            }

            console.info(`[FinancialCategoryService] Seed concluído: ${count} categorias criadas.`);
            return count;
        } catch (error) {
            console.error("Erro no FinancialCategoryService.seedDefaults:", error);
            throw error;
        }
    }
};

export default FinancialCategoryService;
